import { AssetEventActionTypes } from '@electricjs/core_entity-client';
import { TFunction } from 'i18next';

/**
 * Formats a device removed from MDM event action string into a translated description.
 *
 * @param action - The raw action string to format (e.g., "Device was removed from MDM")
 * @param t - The i18next translation function
 * @returns The translated event description string, or null if the action doesn't match the expected format
 *
 * @example
 * Input: "Device was removed from MDM"
 * Output: "L'appareil a été retiré du MDM"
 */
const formatDeviceRemovedEvent = (action: string, t: TFunction) => {
  const translationKey =
    EventDescriptionTranslationKeysEnum.DeviceRemovedFromMdm;

  const prefix = 'Device was removed from MDM';

  if (!action.startsWith(prefix)) {
    return null;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`);
  return result;
};

/**
 * Formats an asset assignment event action string into a translated description with the assignee's name.
 *
 * @param action - The raw action string to format (e.g., "Assign Asset: John Doe")
 * @param t - The i18next translation function
 * @returns The translated event description string with the assignee's name interpolated, or null if the action doesn't match the expected format
 *
 * @example
 * Input: "Assign Asset: John Doe"
 * Output: "L'actif a été assigné à John Doe"
 */
const formatAssignEvent = (action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.Assign;

  const prefix = 'Assign Asset: ';

  if (!action.startsWith(prefix)) {
    return null;
  }
  const namePart = action.slice(prefix.length).trim();
  const [fullName] = namePart.split(/\s+/);

  if (!fullName) {
    return null;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
    fullName,
  });

  return result;
};

/**
 * Formats an asset unassignment event action string into a translated description with the unassigned user's name.
 *
 * @param action - The raw action string to format (e.g., "Unassign Asset: John Doe")
 * @param t - The i18next translation function
 * @returns The translated event description string with the unassigned user's name interpolated, or null if the action doesn't match the expected format
 *
 * @example
 * Input: "Unassign Asset: John Doe"
 * Output: "L'actif a été désassigné de John Doe"
 */
const formatUnassignEvent = (action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.Unassign;

  const prefix = 'Unassign Asset: ';

  if (!action.startsWith(prefix)) {
    return null;
  }
  const namePart = action.slice(prefix.length).trim();
  const [fullName] = namePart.split(/\s+/);

  if (!fullName) {
    return null;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
    fullName,
  });

  return result;
};

/**
 * Formats a site change event action string into a translated description with old and new site names.
 *
 * @param action - The raw action string to format (e.g., "Site changed from New York to Los Angeles")
 * @param t - The i18next translation function
 * @returns The translated event description string with old and new site names interpolated, or null if the action doesn't match the expected format
 *
 * @example
 * Input: "Site changed from New York to Los Angeles"
 * Output: "Site changé de New York à Los Angeles"
 */
const formatChangeSiteEvent = (action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.ChangeSite;

  const prefix = 'Site changed from ';
  const separator = ' to ';

  if (!action.startsWith(prefix)) {
    return null;
  }

  const rest = action.slice(prefix.length);
  const separatorIndex = rest.indexOf(separator);

  if (separatorIndex === -1) {
    return null;
  }

  const oldSite = rest.slice(0, separatorIndex).trim();
  const newSite = rest.slice(separatorIndex + separator.length).trim();

  if (!oldSite || !newSite) {
    return null;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
    oldSite,
    newSite,
  });

  return result;
};

/**
 * Determines if an action string has the change status prefix (i.e., is not a new device registration event).
 *
 * @param action - The raw action string to check
 * @returns true if the action has a change status prefix (is a status change event), false if it's a new device registration event
 *
 * @example
 * Input: "Status changed from Active to Inactive"
 * Output: true
 *
 * @example
 * Input: "A new device has been registered in our system"
 * Output: false
 */
const hasChangeStatusPrefix = (action: string) => {
  const newDeviceEvent = 'A new device has been registered in our system';

  let hasPrefix = false;
  if (action === newDeviceEvent) {
    hasPrefix = false;
    return hasPrefix;
  }
  hasPrefix = true;
  return hasPrefix;
};

/**
 * Formats a status change or new device registration event action string into a translated description.
 * Handles both status change events (with old and new status) and new device registration events.
 *
 * @param action - The raw action string to format (e.g., "Status changed from Active to Inactive" or "A new device has been registered in our system")
 * @param t - The i18next translation function
 * @returns The translated event description string. For status changes, includes old and new status interpolated. For new device events, returns a simple translated message. Returns null if the action doesn't match the expected format
 *
 * @example
 * Input: "Status changed from Active to Inactive"
 * Output: "Statut changé de Active à Inactive"
 *
 * @example
 * Input: "A new device has been registered in our system"
 * Output: "Un nouvel appareil a été enregistré dans notre système."
 */
const formatChangeStatusEvent = (action: string, t: TFunction) => {
  if (hasChangeStatusPrefix(action)) {
    const changeStatusPrefix = 'Status changed from ';
    const separator = ' to ';
    if (!action.startsWith(changeStatusPrefix)) {
      return null;
    }
    const rest = action.slice(changeStatusPrefix.length);
    const separatorIndex = rest.indexOf(separator);

    if (separatorIndex === -1) {
      return null;
    }

    const oldStatus = rest.slice(0, separatorIndex).trim();
    const newStatus = rest.slice(separatorIndex + separator.length).trim();

    if (!oldStatus || !newStatus) {
      return null;
    }
    const translationKey = EventDescriptionTranslationKeysEnum.ChangeStatus;
    const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
      oldStatus,
      newStatus,
    });

    return result;
  }

  const translationKey = EventDescriptionTranslationKeysEnum.NewDevice;
  const result = t(`eventsHistory.eventDescriptions.${translationKey}`);

  return result;
};

/**
 * Formats a device issue created event action string into a translated description with the issue type.
 *
 * @param action - The raw action string to format (e.g., "Battery issue created on this device.")
 * @param t - The i18next translation function
 * @returns The translated event description string with the issue type interpolated, or null if the action doesn't match the expected format
 *
 * @example
 * Input: "Battery issue created on this device."
 * Output: "Un problème Battery a été créé sur cet appareil."
 */
const formatDeviceIssueCreatedEvent = (action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.DeviceIssueCreated;

  const suffix = 'issue created on this device.';

  if (!action.includes(suffix)) {
    return null;
  }

  const deviceIssueType = action.slice(0, -suffix.length).trim();

  if (!deviceIssueType) {
    return null;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
    deviceIssueType,
  });

  return result;
};

/**
 * Formats a device issue ignored event action string into a translated description with the issue type.
 *
 * @param action - The raw action string to format (e.g., "Battery issue ignored on this device.")
 * @param t - The i18next translation function
 * @returns The translated event description string with the issue type interpolated, or null if the action doesn't match the expected format
 *
 * @example
 * Input: "Battery issue ignored on this device."
 * Output: "Un problème Battery a été ignoré sur cet appareil."
 */
const formatDeviceIssueIgnoredEvent = (action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.DeviceIssueIgnored;

  const suffix = 'issue ignored on this device.';

  if (!action.includes(suffix)) {
    return null;
  }

  const deviceIssueIgnoredType = action.slice(0, -suffix.length).trim();

  if (!deviceIssueIgnoredType) {
    return null;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
    deviceIssueIgnoredType,
  });

  return result;
};

/**
 * Placeholder formatter function for event types that are not yet implemented.
 * Used to prevent breaking the mapActionTypeToEventDescription mapping.
 *
 * @param action - The raw action string (unused, logged for debugging)
 * @param t - The i18next translation function (unused, logged for debugging)
 * @returns Always returns null
 */
const notImplementedFormatEvent = (_action: string, _t: TFunction) => {
  return null;
};

/**
 * Formats a device health issue event action string into a translated description.
 * Handles connection lost/restored events and device health issue detected/ignored events.
 *
 * @param action - The raw action string to format (e.g., "Connection to Electric application has been lost on this device." or "Battery has been detected on this device.")
 * @param t - The i18next translation function
 * @returns The translated event description string. For issue detected/ignored events, includes the issue type interpolated. For connection events, returns a simple translated message. Returns null if the action doesn't match any expected format
 *
 * @example
 * Input: "Connection to Electric application has been lost on this device."
 * Output: "La connexion à l'application Electric a été perdue sur cet appareil."
 *
 * @example
 * Input: "Battery has been detected on this device."
 * Output: "Un problème Battery a été détecté sur cet appareil."
 *
 * @example
 * Input: "Battery issue ignored on this device."
 * Output: "Un problème Battery a été ignoré sur cet appareil."
 */
const formatDeviceHealthIssueEvent = (action: string, t: TFunction) => {
  const { translationKey, hasPrefix } = hasDeviceHealthIssuePrefix(action);

  if (hasPrefix) {
    const params = getDeviceHealthIssueParams(action);
    if (!params) {
      return null;
    }
    const { issueType, translationKey } = params;
    const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
      issueType,
    });
    return result;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`);

  return result;
};

/**
 * Determines if an action string matches a connection event or requires parameter extraction for device health issues.
 *
 * @param action - The raw action string to check
 * @returns An object with translationKey (null for parameterized events) and hasPrefix (false for connection events, true for parameterized events)
 *
 * @example
 * Input: "Connection to Electric application has been lost on this device."
 * Output: { translationKey: EventDescriptionTranslationKeysEnum.ConnectionLost, hasPrefix: false }
 *
 * @example
 * Input: "Battery has been detected on this device."
 * Output: { translationKey: null, hasPrefix: true }
 */
const hasDeviceHealthIssuePrefix = (action: string) => {
  const connectionLostEvent =
    'Connection to Electric application has been lost on this device.';

  const appConnectionRestoredEvent =
    'Electric application connection has been restored on this device.';
  const hasPrefix = false;

  if (action === connectionLostEvent) {
    return {
      translationKey: EventDescriptionTranslationKeysEnum.ConnectionLost,
      hasPrefix,
    };
  }

  if (action === appConnectionRestoredEvent) {
    return {
      translationKey: EventDescriptionTranslationKeysEnum.ConnectionRestored,
      hasPrefix,
    };
  }

  return {
    translationKey: null,
    hasPrefix: true,
  };
};

/**
 * Extracts the issue type and translation key from a device health issue action string.
 *
 * @param action - The raw action string to parse (e.g., "Battery issue ignored on this device." or "Battery has been detected on this device.")
 * @returns An object with issueType and translationKey, or null if the action doesn't match the expected format
 *
 * @example
 * Input: "Battery issue ignored on this device."
 * Output: { issueType: "Battery", translationKey: EventDescriptionTranslationKeysEnum.DeviceHealthIssueIgnored }
 *
 * @example
 * Input: "Battery has been detected on this device."
 * Output: { issueType: "Battery", translationKey: EventDescriptionTranslationKeysEnum.DeviceHealthIssueDetected }
 */
const getDeviceHealthIssueParams = (action: string) => {
  const deviceIssueIgnoredSuffix = 'issue ignored on this device.';
  const deviceIssueDetectedSuffix = 'has been detected on this device.';
  const deviceIssueElectricEmailPrefix =
    'Electric sent an email regarding this ';

  const deviceIssueResolvedSuffix = 'issue resolved on this device.';

  if (action.includes(deviceIssueIgnoredSuffix)) {
    const deviceHealthIssueType = action
      .slice(0, -deviceIssueIgnoredSuffix.length)
      .trim();
    const translationKey =
      EventDescriptionTranslationKeysEnum.DeviceHealthIssueIgnored;
    return {
      issueType: deviceHealthIssueType,
      translationKey,
    };
  }

  if (action.includes(deviceIssueDetectedSuffix)) {
    const deviceHealthIssueType = action
      .slice(0, -deviceIssueDetectedSuffix.length)
      .trim();
    const translationKey =
      EventDescriptionTranslationKeysEnum.DeviceHealthIssueDetected;
    return {
      issueType: deviceHealthIssueType,
      translationKey,
    };
  }
  if (action.includes(deviceIssueResolvedSuffix)) {
    const deviceHealthIssueType = action
      .slice(0, -deviceIssueResolvedSuffix.length)
      .trim();
    const translationKey =
      EventDescriptionTranslationKeysEnum.DeviceHealthIssueResolved;
    return {
      issueType: deviceHealthIssueType,
      translationKey,
    };
  }
  if (action.includes(deviceIssueElectricEmailPrefix)) {
    const suffix = 'issue.';
    const deviceHealthIssueType = action
      .slice(deviceIssueElectricEmailPrefix.length, -suffix.length)
      .trim();
    const translationKey =
      EventDescriptionTranslationKeysEnum.DeviceHealthIssueElectricEmail;
    return {
      issueType: deviceHealthIssueType,
      translationKey,
    };
  }
  return null;
};

/**
 * Formats a file deletion event action string into a translated description with the file name.
 *
 * @param action - The raw action string to format (e.g., "File image.jpg deleted.")
 * @param t - The i18next translation function
 * @returns The translated event description string with the file name interpolated, or null if the action doesn't match the expected format
 *
 * @example
 * Input: "File image.jpg deleted."
 * Output: "Fichier image.jpg supprimé."
 */
const formatDeleteImageEvent = (action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.DeleteImage;

  const prefix = 'File ';
  const suffix = ' deleted.';

  if (!action.startsWith(prefix) || !action.endsWith(suffix)) {
    return null;
  }

  const fileName = action
    .slice(prefix.length, action.length - suffix.length)
    .trim();

  if (!fileName) {
    return null;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
    fileName,
  });

  return result;
};

/**
 * Formats a file upload event action string into a translated description with the file name.
 *
 * @param action - The raw action string to format (e.g., "File image.jpg uploaded.")
 * @param t - The i18next translation function
 * @returns The translated event description string with the file name interpolated, or null if the action doesn't match the expected format
 *
 * @example
 * Input: "File image.jpg uploaded."
 * Output: "Fichier image.jpg téléversé."
 */
const formatUploadImageEvent = (action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.UploadImage;

  const prefix = 'File ';
  const suffix = ' uploaded.';

  if (!action.startsWith(prefix) || !action.endsWith(suffix)) {
    return null;
  }

  const fileName = action
    .slice(prefix.length, action.length - suffix.length)
    .trim();

  if (!fileName) {
    return null;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
    fileName,
  });

  return result;
};

/**
 * Formats a device unenroll requested event action string into a translated description.
 *
 * @param _action - The raw action string (unused, as this event type has a fixed message)
 * @param t - The i18next translation function
 * @returns The translated event description string
 *
 * @example
 * Input: "Any action string" (unused)
 * Output: "La désinscription de l'appareil a été demandée."
 */
const formatDeviceUnenrollRequestedEvent = (_action: string, t: TFunction) => {
  const translationKey =
    EventDescriptionTranslationKeysEnum.DeviceUnenrollRequested;
  const result = t(`eventsHistory.eventDescriptions.${translationKey}`);
  return result;
};

/**
 * Formats a device unenroll completed event action string into a translated description.
 *
 * @param _action - The raw action string (unused, as this event type has a fixed message)
 * @param t - The i18next translation function
 * @returns The translated event description string
 *
 * @example
 * Input: "Any action string" (unused)
 * Output: "La désinscription de l'appareil a été terminée."
 */
const formatDeviceUnenrollDoneEvent = (_action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.DeviceUnenrollDone;
  const result = t(`eventsHistory.eventDescriptions.${translationKey}`);
  return result;
};

/**
 * Formats a device lock/unlock event action string into a translated description.
 * Handles various device lock-related events including lock, unlock, reminders, and Electric-initiated locks.
 *
 * @param action - The raw action string to format (e.g., "Device has been locked." or "Device has been unlocked.")
 * @param t - The i18next translation function
 * @returns The translated event description string, or null if the action doesn't match any known device lock event format
 *
 * @example
 * Input: "Device has been locked."
 * Output: "L'appareil a été verrouillé."
 *
 * @example
 * Input: "Device has been unlocked."
 * Output: "L'appareil a été déverrouillé."
 *
 * @example
 * Input: "Electric has locked this device."
 * Output: "Electric a verrouillé l'appareil."
 *
 * @example
 * Input: "Electric has sent a reminder that the device is still locked after 3 days."
 * Output: "Electric a envoyé un rappel que l'appareil est toujours verrouillé après 3 jours."
 */
const formatDeviceLockEvent = (action: string, t: TFunction) => {
  if (getDeviceLockTranslationKey(action)) {
    const translationKey = getDeviceLockTranslationKey(action);
    const result = t(`eventsHistory.eventDescriptions.${translationKey}`);
    return result;
  }
  return null;
};

/**
 * Maps a device lock action string to its corresponding translation key enum value.
 *
 * @param action - The raw action string to map
 * @returns The corresponding EventDescriptionTranslationKeysEnum value, or null if the action doesn't match any known device lock event
 *
 * @example
 * Input: "Device has been locked."
 * Output: EventDescriptionTranslationKeysEnum.DeviceLock
 *
 * @example
 * Input: "Device has been unlocked."
 * Output: EventDescriptionTranslationKeysEnum.DeviceUnlock
 *
 * @example
 * Input: "Electric has sent a reminder that the device is still locked after 3 days."
 * Output: EventDescriptionTranslationKeysEnum.DeviceLockElectricReminder
 */
const getDeviceLockTranslationKey = (action: string) => {
  switch (action) {
    case 'Device has been locked.':
      return EventDescriptionTranslationKeysEnum.DeviceLock;
    case 'Device has been unlocked.':
      return EventDescriptionTranslationKeysEnum.DeviceUnlock;
    case 'Electric has sent a reminder that the device is still locked after 3 days.':
      return EventDescriptionTranslationKeysEnum.DeviceLockElectricReminder;
    case 'Electric has locked this device.':
      return EventDescriptionTranslationKeysEnum.DeviceLockByElectric;
    default:
      return null;
  }
};

/**
 * Formats a device wipe requested event action string into a translated description.
 *
 * @param _action - The raw action string (unused, as this event type has a fixed message)
 * @param t - The i18next translation function
 * @returns The translated event description string
 *
 * @example
 * Input: "Any action string" (unused)
 * Output: "L'effacement de l'appareil a été demandé. Il sera restauré aux paramètres d'usine lorsque l'appareil sera connecté à Internet."
 */
const formatDeviceWipeRequestedEvent = (_action: string, t: TFunction) => {
  const translationKey =
    EventDescriptionTranslationKeysEnum.DeviceWipeRequested;
  const result = t(`eventsHistory.eventDescriptions.${translationKey}`);
  return result;
};

/**
 * Formats a device wipe completed event action string into a translated description.
 *
 * @param _action - The raw action string (unused, as this event type has a fixed message)
 * @param t - The i18next translation function
 * @returns The translated event description string
 *
 * @example
 * Input: "Any action string" (unused)
 * Output: "L'appareil a été effacé et restauré aux paramètres d'usine."
 */
const formatDeviceWipeDoneEvent = (_action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.DeviceWipeDone;
  const result = t(`eventsHistory.eventDescriptions.${translationKey}`);
  return result;
};

/**
 * Formats a device lock error event action string into a translated description.
 *
 * @param _action - The raw action string (unused, as this event type has a fixed message)
 * @param t - The i18next translation function
 * @returns The translated event description string
 *
 * @example
 * Input: "Any action string" (unused)
 * Output: "Erreur de verrouillage de l'appareil. L'appareil n'est pas inscrit dans le MDM."
 */
const formatDeviceLockErrorEvent = (_action: string, t: TFunction) => {
  const translationKey = EventDescriptionTranslationKeysEnum.DeviceLockError;
  const result = t(`eventsHistory.eventDescriptions.${translationKey}`);
  return result;
};

/**
 * Maps a change MDM status override action string to its corresponding translation key enum value.
 *
 * @param action - The raw action string to map
 * @returns The corresponding EventDescriptionTranslationKeysEnum value, or null if the action doesn't match any known change MDM status override event
 *
 * @example
 * Input: "This device has been marked as exempt from MDM management."
 * Output: EventDescriptionTranslationKeysEnum.ChangeMdmStatusOverrideSet
 *
 * @example
 * Input: "The MDM exemption has been removed from this device"
 * Output: EventDescriptionTranslationKeysEnum.ChangeMdmStatusOverrideCleared
 */
const getChangeMdmStatusOverrideTranslationKey = (action: string) => {
  switch (action) {
    case 'This device has been marked as exempt from MDM management.':
      return EventDescriptionTranslationKeysEnum.ChangeMdmStatusOverrideSet;
    case 'The MDM exemption has been removed from this device':
      return EventDescriptionTranslationKeysEnum.ChangeMdmStatusOverrideCleared;
    default:
      return null;
  }
};

/**
 * Formats a change MDM status override event action string into a translated description.
 * Handles both setting (exempting) and clearing (removing exemption) the MDM status override.
 *
 * @param action - The raw action string to format (e.g., "This device has been marked as exempt from MDM management." or "The MDM exemption has been removed from this device")
 * @param t - The i18next translation function
 * @returns The translated event description string, or null if the action doesn't match any known change MDM status override event format
 *
 * @example
 * Input: "This device has been marked as exempt from MDM management."
 * Output: "Cet appareil a été marqué comme exempté de la gestion MDM."
 *
 * @example
 * Input: "The MDM exemption has been removed from this device"
 * Output: "L'exemption MDM a été supprimée pour cet appareil"
 */
const formatChangeMdmStatusOverrideEvent = (action: string, t: TFunction) => {
  const translationKey = getChangeMdmStatusOverrideTranslationKey(action);

  if (!translationKey) {
    return null;
  }

  const result = t(`eventsHistory.eventDescriptions.${translationKey}`);
  return result;
};

/**
 * Formats a file upload event action string into a translated description with the file name.
 * Handles three types of file upload events: size limit exceeded, upload failed, and upload rejected (malware detected).
 *
 * @param action - The raw action string to format (e.g., "File upload failed: size limit exceeded for image.jpg" or "File upload failed: document.pdf" or "File upload rejected: malware detected in  virus.exe")
 * @param t - The i18next translation function
 * @returns The translated event description string with the file name interpolated, or null if the action doesn't match any expected format
 *
 * @example
 * Input: "File upload failed: size limit exceeded for image.jpg"
 * Output: "Le téléversement de fichier a échoué: taille limite dépassée pour image.jpg"
 *
 * @example
 * Input: "File upload failed: document.pdf"
 * Output: "Le téléversement de fichier a échoué: document.pdf"
 *
 * @example
 * Input: "File upload rejected: malware detected in  virus.exe"
 * Output: "Le téléversement de fichier a été rejeté: malware détecté dans virus.exe"
 */
const formatFileUploadEvent = (action: string, t: TFunction) => {
  const sizeLimitExceededPrefix =
    'File upload failed: size limit exceeded for ';
  const fileUploadFailedPrefix = 'File upload failed: ';
  const fileUploadRejectedPrefix =
    'File upload rejected: malware detected in  ';

  if (action.startsWith(sizeLimitExceededPrefix)) {
    const fileName = action.slice(sizeLimitExceededPrefix.length).trim();
    if (!fileName) {
      return null;
    }
    const translationKey =
      EventDescriptionTranslationKeysEnum.FileUploadSizeLimitExceeded;
    const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
      fileName,
    });
    return result;
  }

  if (action.startsWith(fileUploadFailedPrefix)) {
    const fileName = action.slice(fileUploadFailedPrefix.length).trim();
    if (!fileName) {
      return null;
    }
    const translationKey = EventDescriptionTranslationKeysEnum.FileUploadFailed;
    const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
      fileName,
    });
    return result;
  }

  if (action.startsWith(fileUploadRejectedPrefix)) {
    const fileName = action.slice(fileUploadRejectedPrefix.length).trim();
    if (!fileName) {
      return null;
    }
    const translationKey =
      EventDescriptionTranslationKeysEnum.FileUploadRejected;
    const result = t(`eventsHistory.eventDescriptions.${translationKey}`, {
      fileName,
    });
    return result;
  }

  return null;
};

export enum EventDescriptionTranslationKeysEnum {
  Assign = 'deviceAssigned',
  Unassign = 'deviceUnassigned',
  ChangeSite = 'changeSite',
  ChangeStatus = 'changeStatus',
  DeviceIssueCreated = 'deviceIssueCreated',
  DeviceIssueIgnored = 'deviceIssueIgnored',
  DeviceIssueReminder = 'deviceIssueReminder',
  DeviceIssueResolved = 'deviceIssueResolved',
  DeviceLockTimeout = 'deviceLockTimeout',
  DeviceLockError = 'deviceLockError',
  DeviceLock = 'deviceLock',
  DeviceLockElectricReminder = 'deviceLockElectricReminder',
  DeviceLockByElectric = 'deviceLockByElectric',
  DeviceUnlock = 'deviceUnlock',
  DeviceRemovedFromMdm = 'deviceRemovedFromMdm',
  NewDevice = 'newDevice',
  DeviceHealthIssueIgnored = 'deviceHealthIssueIgnored',
  DeviceHealthIssueDetected = 'deviceHealthIssueDetected',
  DeviceHealthIssueResolved = 'deviceHealthIssueResolved',
  DeviceHealthIssueElectricEmail = 'deviceHealthIssueElectricEmail',
  ConnectionLost = 'connectionLost',
  ConnectionRestored = 'connectionRestored',
  DeleteImage = 'deleteImage',
  UploadImage = 'uploadImage',
  DeviceUnenrollRequested = 'deviceUnenrollRequested',
  DeviceUnenrollDone = 'deviceUnenrollDone',
  DeviceWipeRequested = 'deviceWipeRequested',
  DeviceWipeDone = 'deviceWipeDone',
  FileUploadSizeLimitExceeded = 'fileUploadSizeLimitExceeded',
  FileUploadFailed = 'fileUploadFailed',
  FileUploadRejected = 'fileUploadRejected',
  ChangeMdmStatusOverrideSet = 'changeMdmStatusOverrideSet',
  ChangeMdmStatusOverrideCleared = 'changeMdmStatusOverrideCleared',
}

// Exported for testing purposes
export {
  formatDeviceRemovedEvent,
  formatAssignEvent,
  formatUnassignEvent,
  formatChangeSiteEvent,
  hasChangeStatusPrefix,
  formatChangeStatusEvent,
  formatDeviceIssueCreatedEvent,
  formatDeviceIssueIgnoredEvent,
  notImplementedFormatEvent,
  formatDeviceHealthIssueEvent,
  hasDeviceHealthIssuePrefix,
  getDeviceHealthIssueParams,
  formatDeleteImageEvent,
  formatUploadImageEvent,
  formatDeviceUnenrollRequestedEvent,
  formatDeviceUnenrollDoneEvent,
  formatDeviceLockEvent,
  getDeviceLockTranslationKey,
  formatDeviceWipeRequestedEvent,
  formatDeviceWipeDoneEvent,
  formatDeviceLockErrorEvent,
  formatFileUploadEvent,
  getChangeMdmStatusOverrideTranslationKey,
  formatChangeMdmStatusOverrideEvent,
};

export const mapActionTypeToEventDescription = {
  [AssetEventActionTypes.Assign]: formatAssignEvent,
  [AssetEventActionTypes.Unassign]: formatUnassignEvent,
  [AssetEventActionTypes.ChangeSite]: formatChangeSiteEvent,
  [AssetEventActionTypes.ChangeStatus]: formatChangeStatusEvent,
  [AssetEventActionTypes.DeviceIssueCreated]: formatDeviceIssueCreatedEvent,
  [AssetEventActionTypes.DeviceIssueIgnored]: formatDeviceIssueIgnoredEvent,
  [AssetEventActionTypes.DeviceIssueReminder]: notImplementedFormatEvent, // Coming from the backend as device health issue
  [AssetEventActionTypes.DeviceIssueResolved]: notImplementedFormatEvent, // Coming from the backend as device health issue
  [AssetEventActionTypes.DeviceLock]: formatDeviceLockEvent,
  [AssetEventActionTypes.DeviceLockTimeout]: notImplementedFormatEvent,
  [AssetEventActionTypes.DeviceLockError]: formatDeviceLockErrorEvent,
  [AssetEventActionTypes.DeviceWipeRequested]: formatDeviceWipeRequestedEvent,
  [AssetEventActionTypes.DeviceWipeDone]: formatDeviceWipeDoneEvent,
  [AssetEventActionTypes.DeviceWipeTimeout]: notImplementedFormatEvent,
  [AssetEventActionTypes.DeviceWipeError]: notImplementedFormatEvent,
  [AssetEventActionTypes.UploadImage]: formatUploadImageEvent,
  [AssetEventActionTypes.DeleteImage]: formatDeleteImageEvent,
  [AssetEventActionTypes.FileUploadRejected]: formatFileUploadEvent,
  [AssetEventActionTypes.DeviceUnenrollRequested]:
    formatDeviceUnenrollRequestedEvent,
  [AssetEventActionTypes.DeviceUnenrollDone]: formatDeviceUnenrollDoneEvent,
  [AssetEventActionTypes.DeviceUnenrollError]: notImplementedFormatEvent,
  [AssetEventActionTypes.DeviceRemovedFromMdm]: formatDeviceRemovedEvent,
  [AssetEventActionTypes.ChangeMdmStatusOverride]:
    formatChangeMdmStatusOverrideEvent,
  [AssetEventActionTypes.Firewall]: notImplementedFormatEvent,
  [AssetEventActionTypes.DeviceHealthIssue]: formatDeviceHealthIssueEvent,
};
